import { useEffect, useMemo, useRef, useState } from 'react'
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { ListChecks, Boxes, ChevronLeft, ChevronRight, Cpu, Keyboard, Menu, Radio, Shield, Sparkles, Users, ShoppingBag } from 'lucide-react'
import { Monitor } from './modules/monitor'
import { ProgressDashboard } from './modules/progress'
import { Access, AdminUsers, PlatformRegistry } from './modules/platform'
import { Flash, EyesPreview, Vibokey } from './modules/device'
import { CloudTest, EmoGallery, PublicTest } from './modules/diagnostics'
import { Login, LogoutCallback, OidcCallback } from './modules/auth'
import { useI18n } from './utils/i18n'
import { usePresence } from './utils/presence'
import logoUrl from './assets/images/logo.png'
import {
  AppPreviewAi,
  AppPreviewDevices,
  AppPreviewGallery,
  AppPreviewLayout,
  AppPreviewMine,
} from './modules/app-preview'
import RequireAuth from './views/RequireAuth'
import { loadSession, setFlashToast } from './auth/state'
import { startLogout } from './auth/oidc'
import { useToast } from './components/Toast'
import { LanguageToggle } from './components/ui/LanguageToggle'
import { ThemeToggle } from './components/ui/ThemeToggle'
import { CommerceLayout, CommerceLogistics, CommerceResource, CommerceSystem } from './modules/commerce'

function App() {
  const location = useLocation()
  const mainContentRef = useRef(null)
  const { t } = useI18n()
  const [collapsed, setCollapsed] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const profilePresence = usePresence(profileOpen, { exitMs: 140 })
  const profileExiting = profilePresence.phase === 'exit'
  const { toast } = useToast()
  const isVibokeyHost = String(window.location.hostname || '').toLowerCase() === 'vibokey.chamii.com'

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0
    }
    setSidebarOpen(false)
  }, [location.pathname])

  const pageTitle = useMemo(() => {
    if (location.pathname.startsWith('/progress')) return '开发进度'
    if (location.pathname.startsWith('/admin')) return t('accounts')
    if (location.pathname.startsWith('/flash')) return t('flash')
    if (location.pathname.startsWith('/vibokey')) return t('vibokey')
    if (location.pathname.startsWith('/eyes')) return t('eyes')
    if (location.pathname.startsWith('/access')) return t('access')
    if (location.pathname.startsWith('/platforms')) return t('platform')
    if (location.pathname.startsWith('/commerce')) return t('commerce')
    try {
      if (location.pathname.startsWith('/monitor')) {
        const qs = new URLSearchParams(location.search)
        if (qs.get('panel') === 'story') return t('story')
      }
    } catch {
      // ignore
    }
    return t('monitor')
  }, [location.pathname, location.search, t])

  const navItems = useMemo(() => {
    const items = [
      { id: 'monitor', label: t('monitor'), icon: Radio, path: '/monitor' },
      { id: 'progress', label: '开发进度', icon: ListChecks, path: '/progress' },
      { id: 'vibokey', label: t('vibokey'), icon: Keyboard, path: '/vibokey' },
      { id: 'flash', label: t('flash'), icon: Cpu, path: '/flash' },
      { id: 'eyes', label: t('eyes'), icon: Sparkles, path: '/eyes' },
      { id: 'accounts', label: t('accounts'), icon: Users, path: '/admin/users' },
      { id: 'commerce', label: t('commerce'), icon: ShoppingBag, path: '/commerce' },
      { id: 'platform', label: t('platform'), icon: Boxes, path: '/platforms' },
      { id: 'access', label: t('access'), icon: Shield, path: '/access' },
    ]
    return isVibokeyHost ? items.filter((item) => item.id === 'vibokey') : items
  }, [isVibokeyHost, t])

  const session = loadSession()
  const profileName = session?.user?.name || session?.user?.preferred_username || session?.user?.email || session?.user?.sub || 'Admin'
  const profileEmail = session?.user?.email || ''
  const profileId = session?.user?.sub || ''
  const profileDetails = []
  if (profileEmail && profileEmail !== profileName) {
    profileDetails.push({ label: t('email'), value: profileEmail })
  }
  if (profileId && profileId !== profileName && profileId !== profileEmail) {
    profileDetails.push({ label: t('accountId'), value: profileId })
  }

  const isAuthRoute =
    location.pathname === '/login' ||
    location.pathname === '/oidc/callback' ||
    location.pathname === '/logout/callback'

  const isPublicTestRoute = location.pathname === '/test' || location.pathname.startsWith('/test/')
  const isPublicAppRoute = location.pathname === '/app' || location.pathname.startsWith('/app/')

  if (isAuthRoute) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/oidc/callback" element={<OidcCallback />} />
        <Route path="/logout/callback" element={<LogoutCallback />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  if (isPublicAppRoute) {
    return (
      <Routes>
        <Route path="/app" element={<AppPreviewLayout />}>
          <Route index element={<Navigate to="ai" replace />} />
          <Route path="devices" element={<AppPreviewDevices />} />
          <Route path="gallery" element={<AppPreviewGallery />} />
          <Route path="ai" element={<AppPreviewAi />} />
          <Route path="mine" element={<AppPreviewMine />} />
          <Route path="*" element={<Navigate to="ai" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    )
  }

  if (isPublicTestRoute) {
    return (
      <Routes>
        <Route path="/test/*" element={<PublicTest />} />
        <Route path="*" element={<Navigate to="/test" replace />} />
      </Routes>
    )
  }

  return (
    <RequireAuth>
      <div className={`flex chamii-viewport-h overflow-hidden bg-dots chamii-admin-shell text-brand-textDark dark:text-slate-100 ${isVibokeyHost ? 'vibokey-host-shell' : ''}`}>
        {sidebarOpen ? (
          <button
            type="button"
            className="fixed inset-0 bg-black/30 z-10 md:hidden"
            aria-label="close-sidebar"
            onClick={() => setSidebarOpen(false)}
          />
        ) : null}
        <aside
          className={`chamii-sidebar ${
            collapsed ? 'w-24' : 'w-72'
          } bg-brand-bg/90 dark:bg-slate-950/70 backdrop-blur-xl text-brand-textDark dark:text-slate-100 transition-all duration-300 flex flex-col fixed inset-y-0 left-0 h-full z-20 md:relative shadow-[6px_0_28px_rgba(15,23,42,0.08)] border-r border-white/40 dark:border-slate-800/60 ring-1 ring-black/5 dark:ring-white/5 transition-transform ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0`}
        >
          <div className={`h-24 flex items-center flex-shrink-0 transition-all duration-300 ${collapsed ? 'justify-center' : 'justify-start pl-8'}`}>
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={logoUrl}
                alt={isVibokeyHost ? 'Vibokey' : 'Chamii'}
                className={`${collapsed ? 'w-12 h-12' : 'w-12 h-12'} rounded-2xl object-contain bg-brand-bg border-2 border-brand-textDark/10 shadow-sm`}
              />
              {!collapsed && (
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-brand-textDark dark:text-slate-100 font-extrabold text-2xl tracking-tight truncate">{isVibokeyHost ? 'Vibokey' : t('appTitle')}</div>
                    <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest bg-violet-100 text-violet-900 border border-violet-200">
                      {t('beta')}
                    </span>
                  </div>
                  <div className="text-brand-text dark:text-slate-300 text-xs font-bold truncate">{pageTitle}</div>
                </div>
              )}
            </div>
          </div>

          <nav className="flex-1 py-4 px-4 space-y-6 overflow-y-auto custom-scrollbar">
            <div className="space-y-1">
              {!collapsed && (
                <div className="px-4 text-[11px] font-black text-brand-text uppercase tracking-widest mb-3">{t('platform')}</div>
              )}
              {navItems.map((item) => {
                const Icon = item.icon
                const active = item.id === 'commerce'
                  ? location.pathname.startsWith('/commerce')
                  : location.pathname === item.path
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
		                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 group relative border border-transparent font-bold ${
                        (item.id === 'commerce' ? active : isActive)
                          ? 'bg-brand-primary text-white shadow-[3px_3px_0_#DDD4F2]'
                          : 'text-brand-text dark:text-slate-300 hover:bg-brand-bg/70 dark:hover:bg-slate-900/50 hover:text-brand-textDark dark:hover:text-slate-100'
                      } ${collapsed ? 'justify-center px-2' : ''}`
                    }
                    title={collapsed ? item.label : undefined}
                  >
	                    <Icon
	                      className={`w-6 h-6 flex-shrink-0 transition-colors ${
                        active
	                          ? 'text-white'
	                          : 'text-brand-text group-hover:text-brand-textDark dark:group-hover:text-brand-yellow'
	                      }`}
	                      strokeWidth={2.5}
	                    />
                    {!collapsed && <span className="text-sm truncate">{item.label}</span>}

                    {collapsed && (
                      <div className="absolute left-20 bg-slate-800 text-white text-xs px-3 py-1.5 rounded-xl shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap ml-1 font-bold">
                        {item.label}
                      </div>
                    )}
                  </NavLink>
                )
              })}
            </div>
          </nav>

          <div className="p-6 flex justify-center flex-shrink-0 bg-transparent border-t border-white/40 dark:border-slate-800/60">
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-3 text-brand-text hover:text-brand-textDark rounded-full hover:bg-brand-bg dark:hover:bg-slate-900 transition-colors border-2 border-brand-textDark/10 dark:border-slate-800 hover:border-brand-textDark/20 dark:hover:border-slate-700"
              title={t('collapseSidebar')}
            >
              <ChevronLeft className="w-6 h-6" strokeWidth={3} />
            </button>
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex p-3 text-brand-text hover:text-brand-textDark rounded-full hover:bg-brand-bg dark:hover:bg-slate-900 transition-colors border-2 border-brand-textDark/10 dark:border-slate-800 hover:border-brand-textDark/20 dark:hover:border-slate-700"
              title={collapsed ? t('expandSidebar') : t('collapseSidebar')}
            >
              {collapsed ? <Menu className="w-6 h-6" strokeWidth={3} /> : <ChevronLeft className="w-6 h-6" strokeWidth={3} />}
            </button>
          </div>
		        </aside>

		        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
			          <header className="chamii-topbar h-20 bg-white/60 dark:bg-slate-950/50 backdrop-blur-md border-b border-brand-textDark/10 dark:border-slate-800/60 flex items-center justify-between px-3 sm:px-8 z-10 sticky top-0">
		            <div className="flex items-center gap-2 text-sm font-extrabold text-brand-textDark dark:text-slate-100">
                  <button
                    type="button"
                    className="md:hidden p-2 -ml-2 rounded-xl text-brand-text hover:text-brand-textDark hover:bg-brand-bg dark:hover:bg-slate-900/40 dark:hover:text-slate-100 transition-colors"
                    onClick={() => setSidebarOpen(true)}
                    aria-label="open-sidebar"
                    title={t('expandSidebar')}
                  >
                    <Menu className="w-5 h-5" strokeWidth={3} />
                  </button>
			              <span className="hidden text-brand-textDark dark:text-white text-lg sm:inline">{isVibokeyHost ? 'Vibokey' : t('chamiiAdmin')}</span>
		              <ChevronRight className="hidden w-4 h-4 text-brand-text dark:text-slate-300 sm:block" strokeWidth={3} />
	              <span className="text-brand-textDark dark:text-white capitalize text-lg">{pageTitle}</span>
	            </div>

		            <div className="flex items-center gap-1 sm:gap-3">
	              <ThemeToggle />
	              <LanguageToggle />

              <div
                className="relative"
                onMouseEnter={() => setProfileOpen(true)}
                onMouseLeave={() => setProfileOpen(false)}
                onFocusCapture={() => setProfileOpen(true)}
                onBlurCapture={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setProfileOpen(false)
                }}
              >
	                <button
	                  type="button"
	                  onClick={() => setProfileOpen(true)}
	                  className="flex items-center justify-center focus:outline-none p-1 rounded-full hover:bg-brand-bg dark:hover:bg-slate-950 hover:shadow-sm transition-all border border-transparent hover:border-brand-textDark/10 dark:hover:border-slate-800"
	                  aria-label="profile-menu"
	                  aria-expanded={profileOpen}
                    aria-haspopup="menu"
                    aria-controls="profile-dropdown"
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') setProfileOpen(false)
                    }}
                    title={profileName}
	                >
                  <img
                    src={logoUrl}
                    alt="Admin"
                    className="w-10 h-10 rounded-full border-2 border-brand-textDark/10 shadow-md bg-brand-bg object-contain"
                  />
	                </button>

                {profilePresence.mounted && (
                  <div
                    className={`absolute right-0 top-full w-72 bg-white dark:bg-slate-950 rounded-3xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.1)] py-4 border-2 border-brand-textDark/10 dark:border-slate-800 z-50 ${
                      profileExiting ? 'chamii-popover-exit pointer-events-none' : 'chamii-popover-enter'
                    }`.trim()}
                    id="profile-dropdown"
                    aria-hidden={!profileOpen}
                  >
                    <div className="px-6 py-2 mb-2">
                      <p className="text-xs font-bold text-brand-text uppercase tracking-wider">{t('signedInAs')}</p>
	                      <p className="text-sm font-black text-brand-textDark dark:text-slate-100 truncate">{profileName}</p>
                      {profileDetails.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {profileDetails.map((item) => (
                            <div key={item.label} className="flex items-center gap-2 text-xs text-brand-text dark:text-slate-400">
                              <span className="font-bold uppercase tracking-wide">{item.label}</span>
                              <span className="truncate">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
	                    </div>
		                    <div className="h-0.5 bg-brand-textDark/10 dark:bg-slate-800 my-2 mx-6 rounded-full" />
                    <NavLink
                      to="/admin/users"
                      onClick={() => setProfileOpen(false)}
                      className="w-full text-left px-6 py-3 text-sm font-bold text-brand-text dark:text-slate-200 hover:bg-brand-bg dark:hover:bg-slate-900 hover:text-brand-textDark dark:hover:text-slate-100 flex items-center gap-3"
                    >
                      {t('accountSettings')}
                    </NavLink>
		                    <button
	                      type="button"
	                      onClick={() => {
                        setProfileOpen(false)
                        setFlashToast({ kind: 'success', title: t('signedOut'), message: t('logoutSuccessful') })
                        toast({ kind: 'success', title: t('signedOut'), message: t('logoutSuccessful') })
                        startLogout()
                      }}
	                      className="w-full text-left px-6 py-3 text-sm font-bold text-brand-text dark:text-slate-300 hover:bg-brand-bg dark:hover:bg-slate-900 hover:text-brand-pink flex items-center gap-3"
	                    >
	                      {t('signOut')}
		                    </button>
	                  </div>
	                )}
              </div>
            </div>
          </header>

          <main ref={mainContentRef} className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-8 lg:p-10">
            <div key={location.pathname} className="page-transition-enter max-w-7xl mx-auto">
              <Routes>
                <Route path="/" element={<Navigate to={isVibokeyHost ? "/vibokey" : "/monitor"} replace />} />
                <Route path="/monitor" element={<Monitor />} />
                <Route path="/progress" element={<ProgressDashboard />} />
                <Route path="/story" element={<Navigate to="/monitor?panel=story" replace />} />
                <Route path="/story-editor" element={<Navigate to="/monitor?panel=story&tab=editor" replace />} />
                <Route path="/vibokey" element={<Vibokey />} />
                <Route path="/flash" element={<Flash />} />
                <Route path="/eyes" element={<EyesPreview />} />
                <Route path="/test" element={<Navigate to="/monitor" replace />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/commerce" element={<CommerceLayout />}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<CommerceSystem section="overview" />} />
                  <Route path="orders" element={<CommerceResource section="orders" />} />
                  <Route path="payments" element={<CommerceResource section="payments" />} />
                  <Route path="products" element={<CommerceResource section="products" />} />
                  <Route path="fulfillment" element={<CommerceLogistics section="fulfillment" />} />
                  <Route path="tracking" element={<CommerceLogistics section="tracking" />} />
                  <Route path="exceptions" element={<CommerceLogistics section="exceptions" />} />
                  <Route path="operations" element={<CommerceSystem section="operations" />} />
                  <Route path="integrations" element={<CommerceSystem section="integrations" />} />
                  <Route path="users" element={<CommerceSystem section="users" />} />
                  <Route path="settings" element={<Navigate to="/commerce/settings/overview" replace />} />
                  <Route path="settings/overview" element={<CommerceSystem section="settings" />} />
                  <Route path="settings/payments" element={<CommerceSystem section="payment-settings" />} />
                  <Route path="settings/logistics" element={<CommerceSystem section="logistics-settings" />} />
                  <Route path="notifications" element={<CommerceSystem section="notifications" />} />
                  <Route path="updates" element={<CommerceSystem section="updates" />} />
                  <Route path="*" element={<Navigate to="overview" replace />} />
                </Route>
                <Route path="/platforms" element={<PlatformRegistry />} />
                <Route path="/access" element={<Access />} />
                <Route path="/emo" element={<Navigate to="/monitor" replace />} />
                <Route path="/chat" element={<Navigate to="/monitor" replace />} />
                <Route path="*" element={<Navigate to={isVibokeyHost ? "/vibokey" : "/monitor"} replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </RequireAuth>
  )
}

export default App
